import type { StatusChipColor } from "components/StatusChip";

import {
  EmailIntakeSenderMatchType,
  ReceivedEmailAttachmentCode,
  ReceivedEmailOutcome,
  ReceivedEmailRejectionReason,
} from "generated-graphql/graphql";
import { prettyPrintDateTimeMed, relativeLogTime } from "util/dates";

import type { InboundEmailRow, SenderRow } from "./senderDialogValues";

/**
 * The tab's display logic, kept out of the components so it can be tested directly.
 *
 * These are the strings a reader actually acts on — whether a sender is still in use,
 * whether an email was filed or refused and why — and every one of them has a wrong
 * answer that looks plausible. One already shipped: an entry with mail but no
 * documents read "No mail yet".
 */

/**
 * Why an email was refused, short enough to sit in a chip.
 *
 * Terse on purpose: a sentence-long label made the chip wider than its column, and
 * the cell clipped it. Every reason carries the full explanation in REJECTION_HELP,
 * which is where the detail belongs — the chip only has to be recognisable at a
 * glance. Still never the enum name, which says what the code decided rather than
 * what to do about it.
 */
export const REJECTION_LABELS: Record<ReceivedEmailRejectionReason, string> = {
  [ReceivedEmailRejectionReason.SenderNotAllowed]: "Not allowlisted",
  [ReceivedEmailRejectionReason.SenderDisabled]: "Switched off",
  [ReceivedEmailRejectionReason.AmbiguousTenant]: "Ambiguous sender",
  [ReceivedEmailRejectionReason.RecipientMismatch]: "Wrong address",
  [ReceivedEmailRejectionReason.SesVerdictFailed]: "Failed checks",
  [ReceivedEmailRejectionReason.MalformedFrom]: "Unreadable sender",
};

/** Every reason has one, since the labels above no longer explain themselves. */
export const REJECTION_HELP: Record<ReceivedEmailRejectionReason, string> = {
  [ReceivedEmailRejectionReason.SenderNotAllowed]:
    "No allowlist entry matched this address or its domain, so the email was dropped without a reply.",
  [ReceivedEmailRejectionReason.SenderDisabled]:
    "An allowlist entry matched but is turned off. Mail is refused rather than routed to whoever holds a broader entry.",
  [ReceivedEmailRejectionReason.AmbiguousTenant]:
    "More than one organization allowlists this sender, so routing it would be a guess. Remove one of the entries.",
  [ReceivedEmailRejectionReason.RecipientMismatch]:
    "Delivered to an address that is not this environment's manifest intake address.",
  [ReceivedEmailRejectionReason.SesVerdictFailed]:
    "The SES spam, virus, DKIM or DMARC verdicts did not all pass, so the message was refused even though its sender is allowlisted.",
  [ReceivedEmailRejectionReason.MalformedFrom]:
    "The From header was absent, repeated, or could not be parsed, so there was no sender to match against.",
};

/**
 * "4 days ago" close in, a timestamp once that stops being useful.
 *
 * Delegates to the app's `relativeLogTime`, whose default cutoff is the same seven
 * days this used to hardcode with date-fns. The question it answers is "is this entry
 * still live, or config nobody has cleaned up" — recency matters more than the exact
 * moment, which the tooltip carries.
 */
export function relativeReceived(value: string | null | undefined): string {
  if (!value) return "Never";
  return relativeLogTime(value);
}

/**
 * One chip's worth of text for how an entry matches.
 *
 * Subdomain scope is part of what the match *is*, not a footnote about it, so it
 * reads on the same line — a domain that accepts subdomains and one that does not
 * are two different grants, and splitting them across two lines made the narrower
 * one look like the default with an annotation.
 */
export function matchLabel(row: SenderRow): string {
  if (row.matchType === EmailIntakeSenderMatchType.Address) return "Address";
  return row.includeSubdomains ? "Domain + subdomains" : "Domain";
}

/**
 * When a message arrived. The app's medium datetime, rendered in the reader's own
 * timezone — "Aug 28, 2026, 8:33 AM".
 *
 * This was a hand-rolled "Today 8:33 AM" / "Aug 28 · 8:33 AM", which read nicely but
 * was a fourth date format on a page that already had three, in a different library
 * from every other date in the app.
 */
export function receivedLabel(value: string): string {
  return prettyPrintDateTimeMed(value);
}

/** "2 of 2 accepted", or a dash when nothing was attached to begin with. */
export function attachmentsLabel(row: InboundEmailRow): string {
  const total = row.attachmentsTotalCount;
  if (total == null || total === 0) return "—";
  return `${row.attachmentsProcessedCount ?? 0} of ${total} accepted`;
}

/**
 * Why one attachment was refused, or the note carried by an accepted one.
 *
 * Written for the person reading the staff panel, not the sender: the reply already
 * explains how to fix it, and this only has to say what happened.
 */
export const ATTACHMENT_CODE_LABELS: Record<ReceivedEmailAttachmentCode, string> = {
  [ReceivedEmailAttachmentCode.NoFilename]: "No filename",
  [ReceivedEmailAttachmentCode.NotPdf]: "Not a PDF",
  [ReceivedEmailAttachmentCode.FiletypeMismatch]: "Extension says PDF, contents do not",
  [ReceivedEmailAttachmentCode.DuplicateName]: "Another attachment had this filename",
  [ReceivedEmailAttachmentCode.DuplicateFile]: "Identical copy attached more than once",
  // Ours, not the sender's. Worth saying plainly so nobody asks them to resend.
  [ReceivedEmailAttachmentCode.InternalServerError]: "We failed to process it",
};

/**
 * What the outcome chip says, and how loudly.
 *
 * Reads the stored `outcome` rather than re-deriving one from `status` and the two
 * counts. The derivation used to live here, which meant the label existed only in the
 * browser: the grid pages server-side, so "show me every partial delivery" could not
 * be asked. It is a column now, and this only has to name it.
 *
 * Colour is a severity ladder, and nothing that failed to land is grey: green filed,
 * blue still in flight, amber something did not arrive, red nothing arrived or we
 * broke. A refusal is the system working correctly, but from the reader's side it is
 * still a manifest that did not reach the customer — drawing eleven of them in grey
 * said "nothing to see here" about the rows this page exists to surface.
 *
 * Red stays for the two total losses, so a routine refusal still does not read like an
 * outage.
 */
export function outcomeDisplay(row: InboundEmailRow): {
  color: StatusChipColor;
  label: string;
  help?: string;
} {
  switch (row.outcome) {
    case ReceivedEmailOutcome.Filed:
      return { color: "success", label: "Filed" };

    case ReceivedEmailOutcome.Partial: {
      const total = row.attachmentsTotalCount ?? 0;
      const failed = total - (row.attachmentsProcessedCount ?? 0);
      return {
        color: "warning",
        label: `Partial · ${failed} failed`,
        help: `${failed} of ${total} attachments could not be filed. Expand the row to see which, and why.`,
      };
    }

    case ReceivedEmailOutcome.NothingFiled:
      return {
        color: "error",
        label: "Nothing filed",
        help: "Attachments arrived and none could be filed. The sender was told which failed and why.",
      };

    // Previously reported as a success: the sender was told nothing had been done
    // while this read "Filed".
    case ReceivedEmailOutcome.NoAttachments:
      return {
        color: "warning",
        label: "No attachments",
        help: "The message arrived with nothing attached. The sender was told so.",
      };

    case ReceivedEmailOutcome.Refused: {
      const reason = row.rejectionReason;
      return {
        color: "warning",
        label: reason ? REJECTION_LABELS[reason] : "Refused",
        help: reason ? REJECTION_HELP[reason] : undefined,
      };
    }

    case ReceivedEmailOutcome.Errored:
      return {
        color: "error",
        label: "Errored",
        help: "Processing failed unexpectedly after the sender was accepted. Engineering is alerted to these.",
      };

    default:
      return { color: "info", label: "Processing" };
  }
}

/**
 * What to say about the acknowledgement when there is no reply row.
 *
 * Two different facts wear the same empty value. We deliberately never answer a
 * sender we could not place, so for refused mail "none" is the system working. For
 * mail we did place, "none" means only that no reply was recorded — the message may
 * predate reply tracking — and claiming the sender was unplaceable would be false
 * about an email that reached a customer.
 */
export function missingReplyText(outcome: ReceivedEmailOutcome, attributed: boolean): string {
  if (outcome === ReceivedEmailOutcome.Refused || !attributed) {
    return "None — we never answer a sender we could not place";
  }
  return "Not recorded";
}

/**
 * Why the per-attachment list is empty, when the counts say there were attachments.
 *
 * Silence here would reproduce the problem the list exists to solve: a reader sees
 * "3 of 4 accepted" and nothing about the one that failed. Messages received before
 * this was recorded genuinely have no detail, and saying so is the difference between
 * "we did not keep it" and a page that looks broken.
 */
export function missingAttachmentDetailText(total: number | null | undefined): string | null {
  if (!total || total === 0) return null;
  return "Per-attachment detail was not recorded for this message.";
}

/**
 * The exact moment, for a tooltip behind a relative label.
 *
 * "4 days ago" answers "is this entry still in use" faster, but someone correlating
 * against a customer's report needs the timestamp itself.
 */
export function exactTimestamp(value: string | null | undefined): string {
  if (!value) return "Never";
  return prettyPrintDateTimeMed(value);
}
